// src/components/SimpleForm.jsx
import React from "react";
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Typography,
  Autocomplete,
} from "@mui/material";
import { Formik } from "formik";
import useMediaQuery from "@mui/material/useMediaQuery";

export default function SimpleForm({
  title,
  subtitle,
  Header,
  fields,
  initialValues,
  validation,
  onSubmit,
  submitLabel = "Save",
  columns = 4,
  renderDisplay,
}) {
  const isNonMobile = useMediaQuery("(min-width:600px)");

  const renderField = (f, bag) => {
    const { values, errors, touched, handleBlur, handleChange, setFieldValue } =
      bag;
   const common = {
  fullWidth: true,
  variant: "filled",
  name: f.name,
  label: f.label,
  value: values[f.name],
  onBlur: handleBlur,

  onChange: (e) => {
    // ALWAYS update form first
    handleChange(e);

    // THEN run custom logic safely
    if (f.onChange) {
      f.onChange(e, bag);
    }
  },

  error: !!touched[f.name] && !!errors[f.name],
  helperText: touched[f.name] && errors[f.name],
  sx: { gridColumn: `span ${f.span || columns}`, minWidth: 0 },

  // 👇 IMPORTANT: spread props AFTER but REMOVE onChange
  ...(f.props || {}),
};

    switch (f.type) {
      case "autocomplete":
        return (
          <Autocomplete
            key={f.name}
            options={f.options || []}
            getOptionLabel={(opt) => opt.label || ""}
            value={
              (f.options || []).find((o) => o.value === values[f.name]) || null
            }
            onChange={(e, newVal) => {
              const value = newVal?.value || "";

              // ✅ ALWAYS update Formik
              setFieldValue(f.name, value);

              // ✅ ALSO call custom handler if exists
              if (f.props?.onChange) {
                f.props.onChange(
                  { target: { value } }, // fake event
                  bag, // THIS is the fix
                );
              }
            }}
            disabled={f.props?.disabled}
            noOptionsText="No options found"
            isOptionEqualToValue={(opt, val) => opt.value === val.value}
            sx={{ gridColumn: `span ${f.span || columns}` }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={f.label}
                variant="filled"
                error={touched[f.name] && Boolean(errors[f.name])}
                helperText={touched[f.name] && errors[f.name]}
              />
            )}
          />
        );
      case "select":
        return (
          <TextField key={f.name} select {...common}>
            {(f.options || []).map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
        );

      case "checkbox": {
        const errorText = touched[f.name] && errors[f.name];

        const CheckboxInput = React.forwardRef(
          function CheckboxInput(props, ref) {
            const { onFocus, onBlur, ...rest } = props;
            return (
              <Box
                ref={ref}
                tabIndex={0}
                role="checkbox"
                aria-checked={!!values[f.name]}
                aria-label={f.label}
                onFocus={onFocus}
                onBlur={onBlur}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  pt: 1,
                  pb: 0.5,
                  minHeight: 20,
                  minWidth: 0,
                }}
                {...rest}
              >
                <FormControlLabel
                  sx={{
                    m: 0,
                    "& .MuiFormControlLabel-label": {
                      fontSize: 13,
                      fontWeight: 400,
                    },
                  }}
                  control={
                    <Checkbox
                      checked={!!values[f.name]}
                      onChange={(e) => setFieldValue(f.name, e.target.checked)}
                      name={f.name}
                      size="small"
                    />
                  }
                  label={f.label}
                />
              </Box>
            );
          },
        );

        return (
          <TextField
            key={`${f.name}-${values[f.name] ? "1" : "0"}`}
            fullWidth
            variant="filled"
            label={f.label}
            required={f.required}
            error={Boolean(errorText)}
            helperText={errorText}
            InputLabelProps={{
              shrink: true,
              sx: { fontSize: 13 },
            }}
            InputProps={{
              disableUnderline: false,
              inputComponent: CheckboxInput,
            }}
            sx={{
              gridColumn: `span ${f.span || columns}`,
              minWidth: 0,
            }}
          />
        );
      }

      case "checkboxGroup": {
        const selected = Array.isArray(values[f.name]) ? values[f.name] : [];
        const errorText = touched[f.name] && errors[f.name];

        const handleToggle = (val) => (e) => {
          const checked = e.target.checked;
          let next = [...selected];
          if (checked && !next.includes(val)) next.push(val);
          if (!checked) next = next.filter((x) => x !== val);
          setFieldValue(f.name, next);
        };

        // Make it act like a real TextField input
        // Focusable input so MUI manages focus/hover/label correctly
        const CheckboxGroupInput = React.forwardRef(
          function CheckboxGroupInput(props, ref) {
            const { onFocus, onBlur, className, ...rest } = props; // keep MUI classes
            return (
              <Box
                ref={ref}
                tabIndex={0}
                role="group"
                aria-label={f.label}
                onFocus={onFocus}
                onBlur={onBlur}
                className={className} // IMPORTANT: preserve MUI input class
                sx={{
                  width: "100%", // stay inside the field
                  minWidth: 0,
                  boxSizing: "border-box",
                  display: "grid", // responsive grid keeps items inside
                  gap: 1.5,
                  pt: 1,
                  // two columns on small screens, auto-fit up to 4 as space allows
                  gridTemplateColumns: {
                    xs: "repeat(2, minmax(0, 1fr))",
                    sm: "repeat(2, minmax(0, 1fr))",
                    md: "repeat(4, minmax(0, 1fr))",
                  },
                }}
                {...rest}
              >
                {(f.options || []).map((opt) => (
                  <FormControlLabel
                    key={opt.value}
                    control={
                      <Checkbox
                        size="small"
                        checked={selected.includes(opt.value)}
                        onChange={handleToggle(opt.value)}
                        name={f.name}
                      />
                    }
                    label={opt.label}
                    sx={{ m: 0 }} // no extra margins; grid gap handles spacing
                  />
                ))}
              </Box>
            );
          },
        );

        return (
          <TextField
            key={`${f.name}-${selected.join("|") || "empty"}`} // remount on reset
            fullWidth
            variant="filled"
            label={f.label}
            required={f.required}
            error={Boolean(errorText)}
            helperText={errorText}
            InputProps={{
              disableUnderline: false, // keep default underline
              inputComponent: CheckboxGroupInput, // our focusable input
            }}
            sx={{
              gridColumn: `span ${f.span || columns}`,
              minWidth: 0,
              // CRITICAL: let the filled field grow with wrapping content
              "& .MuiInputBase-root": { alignItems: "flex-start" },
            }}
          />
        );
      }

      case "textarea":
        return <TextField key={f.name} multiline minRows={3} {...common} />;

      case "display":
        return (
          <TextField
            key={f.name}
            label={f.label}
            value={f.value ? f.value(bag.values) : "0.00"}
            variant="filled"
            fullWidth
            InputProps={{ readOnly: true }}
            InputLabelProps={{
              sx: (theme) => ({
                color: theme.palette.text.primary,
                fontWeight: 500,
              }),
            }}
            sx={(theme) => ({
              gridColumn: `span ${f.span || 2}`,
              "& .MuiInputBase-root": {
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
              },
              "& .MuiInputBase-input": {
                fontWeight: "bold",
                color: theme.palette.mode === "dark" ? "#90caf9" : "#1976d2",
                fontSize: "1.05rem",
              },
            })}
          />
        );
      default:
        return <TextField key={f.name} type={f.type || "text"} {...common} />;
    }
  };

  return (
    <Box m="20px">
      {Header ? <Header title={title} subtitle={subtitle} /> : null}

      <Formik
        initialValues={initialValues}
        validationSchema={validation}
        onSubmit={onSubmit}
      >
        {(bag) => (
          <form onSubmit={bag.handleSubmit}>
            <Box
              display="grid"
              gap="30px"
              gridTemplateColumns={`repeat(${columns}, minmax(0, 1fr))`}
              sx={{
                "& > div": {
                  gridColumn: isNonMobile ? undefined : `span ${columns}`,
                },
                // Make all children shrink correctly on narrow screens
                minWidth: 0,
              }}
            >
              {fields.map((f) => renderField(f, bag))}
            </Box>

            <Box display="flex" justifyContent="end" mt="20px" gap={2}>
              <Button
                onClick={() => bag.resetForm()}
                disabled={bag.isSubmitting}
                variant="outlined"
              >
                Reset
              </Button>

              <Button
                type="submit"
                disabled={bag.isSubmitting}
                color="secondary"
                variant="contained"
                startIcon={
                  bag.isSubmitting ? <CircularProgress size={18} /> : null
                }
              >
                {submitLabel}
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
}
